/**
 * OnlineMeetingPanel — Calendar & meetings → "Online meeting" (SUN-70, backend SUN-62).
 *
 * Schedules an online meeting in the company's own Zoom or Google Meet account through the
 * existing POST /meetings/scheduled { mode: 'online', provider }. The meeting needs a deal
 * (the server requires a deal, contact or field lead), a time (IST) and a provider.
 *
 * SUN-62's two tables are not applied on dev yet, so the provider routes answer 500. That,
 * and "nothing connected / switched off", all show NOT_SET_UP — never a raw error.
 * Provider settings are admin-only on the server: a non-admin gets 403 there, so for them we
 * show both providers and let the server's answer decide.
 */
import { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { createMeeting, localInputToIso } from '@/api/meetings';

export const NOT_SET_UP = 'Online meetings are not set up yet';
type Provider = 'zoom' | 'google_meet';
const PROVIDERS: { key: Provider; label: string }[] = [
  { key: 'zoom', label: 'Zoom' },
  { key: 'google_meet', label: 'Google Meet' },
];

interface ProviderView { enabled: boolean; connected: boolean }
type Setup = 'checking' | 'none' | 'unknown' | Provider[];

const NOT_SET_UP_CODES = new Set(['provider_not_connected', 'provider_not_enabled']);

function errBody(err: unknown): { status?: number; error?: string } {
  const r = (err as { response?: { status?: number; data?: { error?: unknown } } } | null)?.response;
  return { status: r?.status, error: typeof r?.data?.error === 'string' ? r.data.error : undefined };
}

interface DealOption { id: string; title: string }

export function OnlineMeetingPanel() {
  const [setup, setSetup] = useState<Setup>('checking');
  const [provider, setProvider] = useState<Provider | null>(null);
  const [search, setSearch] = useState('');
  const [deals, setDeals] = useState<DealOption[]>([]);
  const [dealId, setDealId] = useState('');
  const [when, setWhen] = useState('');
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ url: string | null } | null>(null);

  useEffect(() => {
    let alive = true;
    api.get('/integrations/meetings/settings')
      .then((res) => {
        if (!alive) return;
        const v = (res.data?.data ?? {}) as Partial<Record<Provider, ProviderView>>;
        const on = PROVIDERS.map((p) => p.key).filter((k) => v[k]?.enabled && v[k]?.connected);
        setSetup(on.length ? on : 'none');
        if (on.length) setProvider(on[0]);
      })
      .catch((err) => {
        if (!alive) return;
        // 403 = not an admin: settings are hidden from them, the server decides on send.
        if (errBody(err).status === 403) { setSetup('unknown'); setProvider('zoom'); return; }
        setSetup('none');
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (setup === 'checking' || setup === 'none') return;
    const q = search.trim();
    const t = setTimeout(() => {
      api.get('/deals', { params: { limit: 20, ...(q ? { search: q } : {}) } })
        .then((res) => {
          const d = res.data?.data;
          setDeals((d?.deals ?? d ?? []) as DealOption[]);
        })
        .catch(() => setDeals([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, setup]);

  if (setup === 'checking') return <div className="kw-pad kw-small">Checking online meetings…</div>;
  if (setup === 'none') {
    return (
      <div className="kw-pad" data-testid="online-not-set-up">
        <div className="kw-empty">
          <b>{NOT_SET_UP}</b>
          <div className="kw-small">An admin connects the company's Zoom or Google Meet first. Until then, use Meetings with a pasted link.</div>
        </div>
      </div>
    );
  }

  const choices = setup === 'unknown' ? PROVIDERS : PROVIDERS.filter((p) => setup.includes(p.key));
  const iso = localInputToIso(when);
  const ready = !!dealId && !!iso && !!provider && !saving;

  async function submit() {
    if (!ready || !iso || !provider) return;
    setSaving(true);
    setError(null);
    try {
      const m = await createMeeting({
        mode: 'online', provider, dealId, scheduledAt: iso, durationMin: duration,
        notes: notes.trim() || undefined, source: 'web_admin',
      });
      setMade({ url: (m as { meetingUrl?: string | null; joinUrl?: string | null }).meetingUrl ?? (m as { joinUrl?: string | null }).joinUrl ?? null });
      setNotes('');
    } catch (err) {
      const b = errBody(err);
      if (b.status === 500 || (b.error && NOT_SET_UP_CODES.has(b.error))) setError(NOT_SET_UP);
      else if (b.status === 502) setError(`${provider === 'zoom' ? 'Zoom' : 'Google Meet'} did not make the meeting. Try again in a minute.`);
      else setError(b.error && b.status && b.status < 500 ? b.error : 'Could not schedule the meeting. AURA works as normal.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="kw-scroll kw-pad" data-testid="online-meeting">
      <h4 className="kw-h4">Schedule an online meeting</h4>
      {made && (
        <div className="kw-banner kw-ok" role="status" data-testid="online-made">
          Meeting scheduled. {made.url ? <a href={made.url} target="_blank" rel="noreferrer">Join link</a> : 'The join link is on the meeting.'}
        </div>
      )}
      <div className="kw-form">
        <label className="kw-small">Provider</label>
        <div className="kw-tabs kw-sub" role="radiogroup" aria-label="Provider" style={{ border: 0, padding: 0 }}>
          {choices.map((p) => (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={provider === p.key}
              className={`kw-tab${provider === p.key ? ' kw-sub-on' : ''}`}
              onClick={() => setProvider(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <label className="kw-small" htmlFor="om-deal-search">Deal (lead) this meeting is for</label>
        <input id="om-deal-search" className="kw-input" placeholder="Search deals…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="kw-input" value={dealId} onChange={(e) => setDealId(e.target.value)} aria-label="Deal">
          <option value="">— pick a deal —</option>
          {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>

        <label className="kw-small" htmlFor="om-when">When (IST)</label>
        <input id="om-when" className="kw-input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />

        <label className="kw-small" htmlFor="om-dur">How long</label>
        <select id="om-dur" className="kw-input" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
          {[15, 30, 45, 60, 90].map((n) => <option key={n} value={n}>{n} min</option>)}
        </select>

        <label className="kw-small" htmlFor="om-notes">Topic / notes</label>
        <input id="om-notes" className="kw-input" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
      </div>
      {error && <div className="kw-banner" role="status" data-testid="online-error">{error}</div>}
      <button type="button" className="kw-btn" style={{ marginTop: 10 }} onClick={() => void submit()} disabled={!ready}>
        {saving ? 'Scheduling…' : 'Schedule'}
      </button>
    </div>
  );
}
